import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs,
  Center
} from '@mantine/core';
import { 
  IconSearch, 
  IconEdit, 
  IconTrash, 
  IconEye, 
  IconPlus,
  IconUserPlus,
  IconUserMinus,
  IconCreditCard,
  IconReceipt,
  IconMail,
  IconReport,
  IconCalendar,
  IconCalendarEvent,
  IconCalendarCheck,
  IconCalendarX,
  IconTransfer,
  IconX
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { API_URL } from '../../utils/constants';

function FrontDeskListScreenMantine() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'arrival', 'in-house', 'departure'
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Fetch reservations from API
  useEffect(() => {
    fetchReservations();
  }, [startDate, endDate]);

  // Fetch transactions when a reservation is selected
  useEffect(() => {
    if (selectedReservation && selectedReservation.id) {
      console.log(`🔍 Frontend: Selected reservation:`, selectedReservation);
      console.log(`🔍 Frontend: Reservation ID: ${selectedReservation.id}, Res#: ${selectedReservation.res_number}`);
      fetchTransactions(selectedReservation.id);
    } else {
      setTransactions([]);
    }
  }, [selectedReservation]);

  // Reset active tab when a new reservation is selected
  useEffect(() => {
    if (selectedReservation) {
      setActiveTab('details');
    }
  }, [selectedReservation]);

  // Read localStorage values for view mode and filters
  useEffect(() => {
    const frontDeskViewMode = localStorage.getItem('frontDeskViewMode');
    const frontDeskFilterStatus = localStorage.getItem('frontDeskFilterStatus');
    const frontDeskFilterType = localStorage.getItem('frontDeskFilterType');
    const cameFromReservations = localStorage.getItem('cameFromReservations');

    if (frontDeskViewMode) {
      setViewMode(frontDeskViewMode);
      // Set appropriate date range based on view mode
      setDateRangeForViewMode(frontDeskViewMode);
    }
    
    // If user came from Reservations menu, default status to 'all'
    if (cameFromReservations === 'true') {
      setFilterStatus('all');
      localStorage.removeItem('cameFromReservations'); // Clear the flag
    } else if (frontDeskFilterStatus && frontDeskFilterStatus !== 'all') {
      setFilterStatus(frontDeskFilterStatus);
    }
    
    if (frontDeskFilterType && frontDeskFilterType !== 'all') {
      setFilterType(frontDeskFilterType);
    }

    // Listen for changes in localStorage
    const handleStorageChange = () => {
      const newViewMode = localStorage.getItem('frontDeskViewMode');
      const newFilterStatus = localStorage.getItem('frontDeskFilterStatus');
      const newFilterType = localStorage.getItem('frontDeskFilterType');
      const newCameFromReservations = localStorage.getItem('cameFromReservations');

      if (newViewMode && newViewMode !== viewMode) {
        setViewMode(newViewMode);
        setDateRangeForViewMode(newViewMode);
      }
      
      // Handle status filter changes
      if (newCameFromReservations === 'true') {
        setFilterStatus('all');
        localStorage.removeItem('cameFromReservations');
      } else if (newFilterStatus && newFilterStatus !== filterStatus) {
        setFilterStatus(newFilterStatus);
      }
      
      if (newFilterType && newFilterType !== filterType) {
        setFilterType(newFilterType);
      }
    };

    // Listen for custom event
    window.addEventListener('frontDeskViewModeChange', handleStorageChange);
    
    // Also listen for storage events (for cross-tab communication)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('frontDeskViewModeChange', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [viewMode, filterStatus, filterType]);

  // Function to set appropriate date range based on view mode
  const setDateRangeForViewMode = (mode) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    switch (mode) {
      case 'arrival':
        // Show next 7 days for arrivals
        setStartDate(today.toISOString().split('T')[0]);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        setEndDate(nextWeek.toISOString().split('T')[0]);
        break;
      case 'in-house':
        // Show current day for in-house
        setStartDate(today.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case 'departure':
        // Show next 3 days for departures
        setStartDate(today.toISOString().split('T')[0]);
        const next3Days = new Date(today);
        next3Days.setDate(next3Days.getDate() + 3);
        setEndDate(next3Days.toISOString().split('T')[0]);
        break;
      default:
        // 'all' mode - show current week
        setStartDate(today.toISOString().split('T')[0]);
        const endOfWeek = new Date(today);
        endOfWeek.setDate(endOfWeek.getDate() + 7);
        setEndDate(endOfWeek.toISOString().split('T')[0]);
    }
  };

  const clearViewMode = () => {
    setViewMode('all');
    localStorage.removeItem('frontDeskViewMode');
    setDateRangeForViewMode('all');
  };

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      console.log('Fetching reservations with token:', token ? 'Present' : 'Missing');
      
      const queryParams = new URLSearchParams({
        limit: '100',
        sortBy: 'arrival_date',
        sortOrder: 'asc'
      });
      
      // Add search term if provided
      if (searchTerm) queryParams.append('search', searchTerm);
      
      // Add status filter if not 'all'
      if (filterStatus && filterStatus !== 'all') queryParams.append('status', filterStatus);
      
      // Add type filter if not 'all' and not 'chart' (chart is not a valid reservation type)
      if (filterType && filterType !== 'all' && filterType !== 'chart') queryParams.append('type', filterType);
      
      // Add date range filters if set
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);
      
      const url = `${API_URL}/reservations?${queryParams}`;
      console.log('Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Fetched data:', data);
      setReservations(data.reservations || []);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      notifications.show({
        title: 'Error',
        message: `Failed to fetch reservations: ${error.message}`,
        color: 'red'
      });
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (reservationId) => {
    try {
      console.log(`🔍 Frontend: fetchTransactions called with ID: ${reservationId}`);
      setTransactionsLoading(true);
      const token = localStorage.getItem('token');
      console.log(`🔍 Frontend: Token exists: ${token ? 'Yes' : 'No'}`);
      
      const url = `${API_URL}/reservations/${reservationId}/transactions`;
      console.log(`🔍 Frontend: API URL: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`🔍 Frontend: Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log(`🔍 Frontend: Error response: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`🔍 Frontend: Received data:`, data);
      console.log(`🔍 Frontend: Number of transactions: ${data.length}`);
      setTransactions(data);
    } catch (error) {
      console.error('❌ Frontend: Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleActionClick = (action, reservation) => {
    console.log(`${action} clicked for reservation:`, reservation);
    
    // Show notification for the action
    notifications.show({
      title: 'Action Triggered',
      message: `${action} action initiated for reservation ${reservation.res_number}`,
      color: 'blue'
    });
    
    // Add your action handling logic here
  };

  // Since we're now filtering on the server side, we can use reservations directly
  const filteredReservations = reservations;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Future': return 'blue';
      case 'In-House': return 'green';
      case 'Checked-Out': return 'yellow';
      case 'Cancelled': return 'red';
      case 'No-Show': return 'gray';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">Front Desk</Title>
            
            {/* Primary Actions */}
            <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => fetchReservations()}
              leftSection={<IconSearch size="0.8rem" />}
            >
              Find
            </Button>
            <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              onClick={() => console.log('New Reservation clicked')}
              leftSection={<IconPlus size="0.8rem" />}
            >
              Add
            </Button>
            <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Change', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconEdit size="0.8rem" />}
            >
              Change
            </Button>
            <Button 
              variant="subtle" 
              color="cyan"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Confirmation', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconCalendarEvent size="0.8rem" />}
            >
              Confirmation
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Check-in/out buttons */}
            <Button 
              variant="subtle" 
              color="teal"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Check-in', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconUserPlus size="0.8rem" />}
            >
              Check In
            </Button>
            <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Check-out', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconUserMinus size="0.8rem" />}
            >
              Check Out
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Financial buttons */}
            <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Post Charge', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconCreditCard size="0.8rem" />}
            >
              Post Charge
            </Button>
            <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Payment', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Payment
            </Button>
            <Button 
              variant="subtle" 
              color="purple"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Refund', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Refund
            </Button>
            <Button 
              variant="subtle" 
              color="pink"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Folio', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconReport size="0.8rem" />}
            >
              Folio
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Additional buttons */}
            <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Transfer Payment', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconTransfer size="0.8rem" />}
            >
              Transfer Payment
            </Button>
            <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Transfer Charge', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconTransfer size="0.8rem" />}
            >
              Transfer Charge
            </Button>
            <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => selectedReservation && handleActionClick('Cancel', selectedReservation)}
              disabled={!selectedReservation}
              leftSection={<IconX size="0.8rem" />}
            >
              Cancel
            </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>Reservations List</Title>
            </Group>

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group mb="md" gap="sm">
                <TextInput
                  placeholder="Search by name, reservation number, or unit..."
                  leftSection={<IconSearch size="1rem" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ flex: 1 }}
                />
                <TextInput
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  leftSection={<IconCalendar size="1rem" />}
                  style={{ width: '150px' }}
                />
                <TextInput
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  leftSection={<IconCalendar size="1rem" />}
                  style={{ width: '150px' }}
                />
                <Select
                  value={filterStatus}
                  onChange={(value) => setFilterStatus(value)}
                  data={[
                    { value: 'all', label: 'All Status' },
                    { value: 'Future', label: 'Future' },
                    { value: 'In-House', label: 'In-House' },
                    { value: 'Checked-Out', label: 'Checked-Out' },
                    { value: 'Cancelled', label: 'Cancelled' },
                    { value: 'No-Show', label: 'No-Show' }
                  ]}
                  style={{ width: '120px' }}
                />
                <Select
                  value={filterType}
                  onChange={(value) => setFilterType(value)}
                  data={[
                    { value: 'all', label: 'All Types' },
                    { value: 'Paying Guest', label: 'Paying Guest' },
                    { value: 'Owner', label: 'Owner' },
                    { value: 'Comp', label: 'Comp' },
                    { value: 'Timeshare', label: 'Timeshare' }
                  ]}
                  style={{ width: '120px' }}
                />
                <Button color="blue" size="sm" onClick={fetchReservations}>
                  Search
                </Button>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Res #</Table.Th>
                    <Table.Th>Guest Name</Table.Th>
                    <Table.Th>Unit</Table.Th>
                    <Table.Th>Arrival</Table.Th>
                    <Table.Th>Departure</Table.Th>
                    <Table.Th>Nights</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Phone</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredReservations.length > 0 ? filteredReservations.map((reservation) => (
                    <Table.Tr 
                      key={reservation.id} 
                      onClick={() => setSelectedReservation(reservation)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedReservation && selectedReservation.id === reservation.id ? '#ffff00' : '',
                        border: selectedReservation && selectedReservation.id === reservation.id ? '2px solid #ff0000' : ''
                      }}
                    >
                      <Table.Td>{reservation.res_number}</Table.Td>
                      <Table.Td>
                        {reservation.first_name} {reservation.last_name}
                      </Table.Td>
                      <Table.Td>
                        {reservation.unit_number_from_unit || reservation.unit_number || 'N/A'}
                        {!reservation.unit_number_from_unit && !reservation.unit_number && (
                          <Text size="xs" c="red">
                            (No unit data)
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>{formatDate(reservation.arrival_date)}</Table.Td>
                      <Table.Td>{formatDate(reservation.departure_date)}</Table.Td>
                      <Table.Td>{reservation.nights}</Table.Td>
                      <Table.Td>
                        <Badge color={getStatusColor(reservation.status)}>
                          {reservation.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{reservation.type}</Table.Td>
                      <Table.Td>{reservation.phone1}</Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon
                            variant="subtle"
                            color="orange"
                            onClick={(e) => { 
                              e.stopPropagation();
                              handleActionClick('Edit', reservation);
                            }}
                          >
                            <IconEdit size="1rem" />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={(e) => { 
                              e.stopPropagation();
                              handleActionClick('Delete', reservation);
                            }}
                          >
                            <IconTrash size="1rem" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )) : (
                    <Table.Tr>
                      <Table.Td colSpan={10} style={{ textAlign: 'center' }}>
                        <Text c="dimmed">No reservations found matching your criteria.</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Paper>

            {/* Selected Reservation Details */}
            {selectedReservation && (
              <Paper shadow="sm" p="md" radius="md" withBorder>
                <Group justify="space-between" mb="md">
                  <Title order={4}>Reservation Details: {selectedReservation.res_number}</Title>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => setSelectedReservation(null)}
                  >
                    <IconX size="1rem" />
                  </ActionIcon>
                </Group>

                <Tabs value={activeTab} onChange={setActiveTab}>
                  <Tabs.List>
                    <Tabs.Tab value="details">Details</Tabs.Tab>
                    <Tabs.Tab value="guest">Guest Info</Tabs.Tab>
                    <Tabs.Tab value="folio">Folio</Tabs.Tab>
                    <Tabs.Tab value="history">History</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="details" pt="md">
                    <Grid>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Guest Name:</strong> {selectedReservation.first_name} {selectedReservation.last_name}</Text>
                          <Text><strong>Unit:</strong> {selectedReservation.unit_number}</Text>
                          <Text><strong>Arrival:</strong> {formatDate(selectedReservation.arrival_date)}</Text>
                          <Text><strong>Departure:</strong> {formatDate(selectedReservation.departure_date)}</Text>
                          <Text><strong>Nights:</strong> {selectedReservation.nights}</Text>
                          <Text><strong>Status:</strong> 
                            <Badge color={getStatusColor(selectedReservation.status)} ml="xs">
                              {selectedReservation.status}
                            </Badge>
                          </Text>
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Type:</strong> {selectedReservation.type}</Text>
                          <Text><strong>Phone:</strong> {selectedReservation.phone1}</Text>
                          <Text><strong>Email:</strong> {selectedReservation.email}</Text>
                          <Text><strong>Rate Plan:</strong> {selectedReservation.rate_plan}</Text>
                          <Text><strong>Unit Type:</strong> {selectedReservation.unit_type}</Text>
                          <Text><strong>People:</strong> {selectedReservation.people1} adults, {selectedReservation.people2} children</Text>
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Tabs.Panel>

                  <Tabs.Panel value="guest" pt="md">
                    <Text c="dimmed">Guest information and preferences will be displayed here.</Text>
                    <Text size="sm" c="dimmed" mt="xs">This feature will be implemented based on your specific requirements.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="folio" pt="md">
                    <Text size="sm" c="blue" mb="md">🔍 FOLIO TAB IS RENDERING - Check console for logs</Text>
                    {console.log('🔍 Folio tab render - transactionsLoading:', transactionsLoading, 'transactions:', transactions, 'selectedReservation:', selectedReservation)}
                    {transactionsLoading ? (
                      <Center>
                        <Loader size="md" />
                      </Center>
                    ) : transactions.length > 0 ? (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Posting Date</Table.Th>
                            <Table.Th>Posting Code</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th>Amount</Table.Th>
                            <Table.Th>Quantity</Table.Th>
                            <Table.Th>Total</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {transactions.map((transaction) => (
                            <Table.Tr key={transaction.id}>
                              <Table.Td>
                                {transaction.transaction_date 
                                  ? new Date(transaction.transaction_date).toLocaleDateString()
                                  : 'N/A'
                                }
                              </Table.Td>
                              <Table.Td>{transaction.code || 'N/A'}</Table.Td>
                              <Table.Td>{transaction.description || 'N/A'}</Table.Td>
                              <Table.Td>
                                {transaction.amount ? `$${parseFloat(transaction.amount).toFixed(2)}` : 'N/A'}
                              </Table.Td>
                              <Table.Td>{transaction.quantity || 1}</Table.Td>
                              <Table.Td>
                                {transaction.total ? `$${parseFloat(transaction.total).toFixed(2)}` : 'N/A'}
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    ) : (
                      <div>
                        <Text c="dimmed">No transactions found for this reservation.</Text>
                        <Text size="sm" c="dimmed" mt="xs">
                          Debug: Loading={transactionsLoading.toString()}, 
                          Count={transactions.length}, 
                          Reservation ID={selectedReservation?.id}
                        </Text>
                      </div>
                    )}
                  </Tabs.Panel>

                  <Tabs.Panel value="history" pt="md">
                    <Text c="dimmed">Reservation history and activity log will be displayed here.</Text>
                    <Text size="sm" c="dimmed" mt="xs">This feature will be implemented based on your specific requirements.</Text>
                  </Tabs.Panel>
                </Tabs>
              </Paper>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
}

export default FrontDeskListScreenMantine;
